import { absoluteUrl, defaultOgImageUrl, SITE_NAME, SITE_ORIGIN } from './site'

export type RobotsDirective =
  | 'index, follow'
  | 'noindex, nofollow'
  | 'noindex, follow'
  | 'index, nofollow'

export type PageSeo = {
  path: string
  title: string
  description: string
  robots: RobotsDirective
  ogType?: 'website' | 'article'
  ogImageAlt?: string
  h1: string
  /** Short crawlable summary used by prerender fallback shells */
  summary: string
  /** Whether this URL belongs in sitemap.xml */
  sitemap: boolean
  breadcrumbs?: { name: string; path: string }[]
  /** Absolute canonical when this page is hosted on a dedicated origin. */
  canonicalUrl?: string
}

const OG_ALT = 'Perfect Predict — structured Gold and Silver prediction trading'

export const INDEXABLE_PAGES: PageSeo[] = [
  {
    path: '/',
    title: 'Perfect Predict | Structured Gold & Silver Trading on MT5',
    description:
      'Trade Gold and Silver with predefined target, stop and payout parameters. Choose SAFE, BALANCE or AGGRESSIVE, select BUY or SELL, and execute through MetaTrader 5 infrastructure.',
    robots: 'index, follow',
    h1: 'Prediction Trading with Defined Targets.',
    summary:
      'Perfect Predict offers structured prediction trading on Gold and Silver. Choose a strategy profile, select BUY or SELL, and execute through MetaTrader 5 infrastructure via the Client Portal.',
    sitemap: true,
    breadcrumbs: [{ name: 'Home', path: '/' }],
  },
  {
    path: '/about',
    title: 'About Perfect Predict | Structured Trading Platform',
    description:
      'Learn how Perfect Predict delivers structured prediction trading on Gold and Silver with predefined strategy parameters and MetaTrader 5 execution.',
    robots: 'index, follow',
    h1: 'About Perfect Predict',
    summary:
      'Perfect Predict is a structured prediction trading platform. Traders choose BUY or SELL, select SAFE, BALANCE or AGGRESSIVE strategy profiles, and manage accounts in a secure Client Portal with MT5 execution.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'About', path: '/about' },
    ],
  },
  {
    path: '/contact',
    title: 'Contact Perfect Predict | General Enquiries',
    description:
      'Contact Perfect Predict for general enquiries. Account-specific help is available through Client Portal Support after you sign in.',
    robots: 'index, follow',
    h1: 'Contact',
    summary:
      'Use Perfect Predict contact channels for general enquiries. For account-specific assistance, sign in to Client Portal Support.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Contact', path: '/contact' },
    ],
  },
  {
    path: '/support',
    title: 'Perfect Predict Support | Account and Platform Help',
    description:
      'Get help with Perfect Predict account access, verification, funding, withdrawals, trading access and technical support through the Client Portal.',
    robots: 'index, follow',
    h1: 'Support',
    summary:
      'Perfect Predict Support covers account access, verification, funding, withdrawals, trading access and technical issues via the Client Portal.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Support', path: '/support' },
    ],
  },
  {
    path: '/risk-disclosure',
    title: 'Risk Disclosure | Perfect Predict',
    description:
      'Review the important trading, market, execution, leverage and technology risks associated with using Perfect Predict.',
    robots: 'index, follow',
    ogType: 'article',
    h1: 'Risk Disclosure',
    summary:
      'Trading involves risk of loss. Review Perfect Predict risk disclosure covering market, execution, strategy profile and technology risks.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Risk Disclosure', path: '/risk-disclosure' },
    ],
  },
  {
    path: '/terms',
    title: 'Terms of Use | Perfect Predict',
    description:
      'Read the Perfect Predict Terms of Use governing access to the public website and related informational materials.',
    robots: 'index, follow',
    ogType: 'article',
    h1: 'Terms of Use',
    summary: 'Terms of Use for the Perfect Predict public website and related informational materials.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Terms of Use', path: '/terms' },
    ],
  },
  {
    path: '/privacy',
    title: 'Privacy Policy | Perfect Predict',
    description:
      'How Perfect Predict handles personal information collected through the public website and Client Portal interactions.',
    robots: 'index, follow',
    ogType: 'article',
    h1: 'Privacy Policy',
    summary:
      'Privacy Policy explaining how Perfect Predict handles personal information on the public website and in the Client Portal.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Privacy Policy', path: '/privacy' },
    ],
  },
  {
    path: '/cookies',
    title: 'Cookie Policy | Perfect Predict',
    description:
      'Learn how Perfect Predict uses cookies and similar technologies on the public website.',
    robots: 'index, follow',
    ogType: 'article',
    h1: 'Cookie Policy',
    summary: 'Cookie Policy for the Perfect Predict public website.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Cookie Policy', path: '/cookies' },
    ],
  },
  {
    path: '/gold-prediction-trading',
    title: 'Gold Prediction Trading | Perfect Predict',
    description:
      'Trade Gold (XAU/USD) with structured prediction strategies. Choose SAFE, BALANCE or AGGRESSIVE, select BUY or SELL, and execute through MetaTrader 5.',
    robots: 'index, follow',
    h1: 'Gold Prediction Trading',
    summary:
      'Perfect Predict offers structured Gold (XAU/USD) prediction trading with predefined strategy parameters and MetaTrader 5 execution.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Gold Prediction Trading', path: '/gold-prediction-trading' },
    ],
  },
  {
    path: '/silver-prediction-trading',
    title: 'Silver Prediction Trading | Perfect Predict',
    description:
      'Trade Silver (XAG/USD) with structured prediction strategies. Choose SAFE, BALANCE or AGGRESSIVE, select BUY or SELL, and execute through MetaTrader 5.',
    robots: 'index, follow',
    h1: 'Silver Prediction Trading',
    summary:
      'Perfect Predict offers structured Silver (XAG/USD) prediction trading with predefined strategy parameters and MetaTrader 5 execution.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Silver Prediction Trading', path: '/silver-prediction-trading' },
    ],
  },
  {
    path: '/prediction-strategies',
    title: 'SAFE, BALANCE and AGGRESSIVE Strategies | Perfect Predict',
    description:
      'Compare SAFE, BALANCE and AGGRESSIVE strategy profiles. Each profile presents predefined target, stop and illustrative payout parameters — not guaranteed outcomes.',
    robots: 'index, follow',
    h1: 'SAFE, BALANCE and AGGRESSIVE Strategies',
    summary:
      'SAFE, BALANCE and AGGRESSIVE are predefined strategy/risk-reward profiles with illustrative target, stop and payout parameters. They do not guarantee outcomes.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Prediction Strategies', path: '/prediction-strategies' },
    ],
  },
  {
    path: '/how-perfect-predict-works',
    title: 'How Perfect Predict Works | Structured MT5 Trading',
    description:
      'See how Perfect Predict works: choose a strategy, select BUY or SELL, execute through MetaTrader 5, and let target or stop determine the result.',
    robots: 'index, follow',
    h1: 'How Perfect Predict Works',
    summary:
      'Choose a strategy profile, select BUY or SELL, execute through MetaTrader 5 infrastructure, and the selected strategy’s predefined target or stop determines the result.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'How It Works', path: '/how-perfect-predict-works' },
    ],
  },
  {
    path: '/mt5-trading-infrastructure',
    title: 'MT5 Trading Infrastructure | Perfect Predict',
    description:
      'Perfect Predict connects account management in the Client Portal with trade execution through MetaTrader 5 infrastructure.',
    robots: 'index, follow',
    h1: 'MT5 Trading Infrastructure',
    summary:
      'Perfect Predict uses MetaTrader 5 infrastructure for execution while day-to-day account management happens in the secure Client Portal.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'MT5 Infrastructure', path: '/mt5-trading-infrastructure' },
    ],
  },
  {
    path: '/demo-trading-account',
    title: 'Demo Trading Account | Perfect Predict',
    description:
      'Open a Perfect Predict Demo account with a virtual opening balance — no funding required — to explore structured prediction trading before going Live.',
    robots: 'index, follow',
    h1: 'Demo Trading Account',
    summary:
      'Demo accounts include a virtual opening balance and require no funding. Use Demo to explore Perfect Predict before opening a Live account.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Demo Account', path: '/demo-trading-account' },
    ],
  },
  {
    path: '/funding-and-withdrawals',
    title: 'Funding and Withdrawals | Perfect Predict',
    description:
      'Fund a Live Perfect Predict account through the Client Portal using the supported method (Uniwire), and manage withdrawals from the same secure portal.',
    robots: 'index, follow',
    h1: 'Funding and Withdrawals',
    summary:
      'Live accounts are funded through the Client Portal via the supported method (Uniwire). Demo accounts need no funding. Withdrawals are managed in the Client Portal.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Funding and Withdrawals', path: '/funding-and-withdrawals' },
    ],
  },
  {
    path: '/blog',
    title: 'Blog | Perfect Predict Guides',
    description:
      'Educational guides about Perfect Predict: structured prediction trading, Live MT5, Demo vs Live, funding expectations, and risk basics.',
    robots: 'index, follow',
    h1: 'Perfect Predict Blog',
    summary:
      'Read educational Perfect Predict guides on structured prediction trading, Client Portal accounts, MetaTrader 5 execution, and risk basics — not financial advice.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Blog', path: '/blog' },
    ],
  },
  {
    path: '/blog/what-is-perfect-predict',
    title: 'What is Perfect Predict? | Prediction Trading on Live MT5',
    description:
      'What Perfect Predict is and is not: structured Gold and Silver prediction trading, trader-chosen BUY or SELL, Client Portal management, and MetaTrader 5 execution.',
    robots: 'index, follow',
    ogType: 'article',
    h1: 'What is Perfect Predict? Prediction trading on Live MT5',
    summary:
      'Perfect Predict is a structured prediction trading platform with trader-selected direction, predefined strategy profiles, Client Portal account management, and MetaTrader 5 execution. Trading involves risk of loss.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: 'What is Perfect Predict?', path: '/blog/what-is-perfect-predict' },
    ],
  },
  {
    path: '/blog/how-live-deposits-credit-mt5-balance',
    title: 'How Live Deposits Credit Your MT5 Balance | Perfect Predict',
    description:
      'How Perfect Predict Live deposits work in the Client Portal: Uniwire funding, payment confirmation vs Live balance credit, common mistakes, and risk basics before you trade.',
    robots: 'index, follow',
    ogType: 'article',
    h1: 'How Live deposits credit your MT5 balance',
    summary:
      'Live deposits start in the Perfect Predict Client Portal. Payment confirmation and Live balance credit are related steps, not always the same instant. Trading involves risk of loss.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Blog', path: '/blog' },
      {
        name: 'How Live deposits credit',
        path: '/blog/how-live-deposits-credit-mt5-balance',
      },
    ],
  },
  {
    path: '/blog/risk-checklist-before-first-live-deposit',
    title: 'Risk Checklist Before Your First Live Deposit | Perfect Predict',
    description:
      'A practical Perfect Predict checklist before your first Live deposit: Risk Disclosure, Demo vs Live, strategy structure vs guarantees, portal funding, and what to verify after you pay.',
    robots: 'index, follow',
    ogType: 'article',
    h1: 'Risk checklist before your first Live deposit',
    summary:
      'Before funding a Perfect Predict Live account, use this educational checklist. Trading involves risk of loss. Read the Risk Disclosure — this guide does not replace it.',
    sitemap: true,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Blog', path: '/blog' },
      {
        name: 'Risk checklist before Live deposit',
        path: '/blog/risk-checklist-before-first-live-deposit',
      },
    ],
  },
]

export const NON_INDEXABLE_PAGES: PageSeo[] = [
  {
    path: '/login',
    title: `Sign In | ${SITE_NAME}`,
    description: 'Redirecting to the Perfect Predict Client Portal sign-in page.',
    robots: 'noindex, nofollow',
    h1: 'Sign In',
    summary: 'Redirecting to the Client Portal to sign in.',
    sitemap: false,
  },
  {
    path: '/register',
    title: `Create Account | ${SITE_NAME}`,
    description: 'Redirecting to Perfect Predict Client Portal registration.',
    robots: 'noindex, nofollow',
    h1: 'Create Account',
    summary: 'Redirecting to the Client Portal to create an account.',
    sitemap: false,
  },
  {
    path: '/404',
    title: `Page Not Found | ${SITE_NAME}`,
    description: 'The requested Perfect Predict page could not be found.',
    robots: 'noindex, nofollow',
    h1: 'Page Not Found',
    summary: 'This page does not exist. Return to the Perfect Predict homepage.',
    sitemap: false,
  },
  {
    path: '/go/gold',
    title: `Perfect Predict | Trade gold direction`,
    description:
      'Structured gold direction trading. Create your Perfect Predict Client Portal account.',
    robots: 'noindex, nofollow',
    h1: 'Trade market direction with a structured trading instrument',
    summary: 'Ads landing with Client Portal registration.',
    sitemap: false,
  },
]

/** Dedicated InfinityTech ads landing — not in the www sitemap. */
export const TRADE_LANDING_PAGE: PageSeo = {
  path: '/go/trade',
  title: 'Perfect Predict | Structured Prediction Trading through MetaTrader 5',
  description:
    'Choose a structured prediction strategy, select BUY or SELL and execute through MetaTrader 5. Trading involves risk and returns are not guaranteed.',
  robots: 'index, follow',
  canonicalUrl: 'https://perfectpredict.infinity-tech.io/',
  h1: 'Directional Trading with Defined Targets.',
  summary:
    'Perfect Predict structured prediction trading: choose SAFE, BALANCE or AGGRESSIVE, select BUY or SELL, and execute through MetaTrader 5. Operated by InfinityTech. Trading involves risk and returns are not guaranteed.',
  sitemap: false,
  ogImageAlt: 'Perfect Predict — structured prediction trading through MetaTrader 5',
  breadcrumbs: [{ name: 'Perfect Predict', path: '/go/trade' }],
}

const ALL_PAGES = [...INDEXABLE_PAGES, ...NON_INDEXABLE_PAGES, TRADE_LANDING_PAGE]

export function getPageSeo(path: string): PageSeo | undefined {
  const normalized = normalizePath(path)
  return ALL_PAGES.find((p) => p.path === normalized)
}

export function normalizePath(path: string): string {
  if (!path || path === '/') return '/'
  const bare = path.split('?')[0].split('#')[0]
  if (bare.length > 1 && bare.endsWith('/')) return bare.slice(0, -1)
  return bare.startsWith('/') ? bare : `/${bare}`
}

export function buildHeadTags(page: PageSeo): {
  title: string
  description: string
  canonical: string
  robots: string
  og: Record<string, string>
  twitter: Record<string, string>
} {
  const canonical = page.canonicalUrl || absoluteUrl(page.path === '/' ? '/' : page.path)
  const image = defaultOgImageUrl()
  const ogType = page.ogType || 'website'
  const imageAlt = page.ogImageAlt || OG_ALT
  return {
    title: page.title,
    description: page.description,
    canonical,
    robots: page.robots,
    og: {
      'og:title': page.title,
      'og:description': page.description,
      'og:url': canonical,
      'og:type': ogType,
      'og:site_name': SITE_NAME,
      'og:image': image,
      'og:image:alt': imageAlt,
      'og:locale': 'en_US',
    },
    twitter: {
      'twitter:card': 'summary_large_image',
      'twitter:title': page.title,
      'twitter:description': page.description,
      'twitter:image': image,
    },
  }
}

export function sitemapUrls(): string[] {
  return INDEXABLE_PAGES.filter((p) => p.sitemap).map((p) => absoluteUrl(p.path === '/' ? '/' : p.path))
}

export { SITE_ORIGIN, SITE_NAME }
